import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function StoryContent() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("story");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const chapters = [
    {
      labelKey: "storyContent.ch1.label",
      titleKey: "storyContent.ch1.title",
      bodyKeys: ["storyContent.ch1.body.0", "storyContent.ch1.body.1", "storyContent.ch1.body.2"],
      image: "https://readdy.ai/api/search-image?query=Abstract%20dark%20digital%20cloud%20computing%20concept%20with%20scattered%20data%20particles%20and%20faint%20red%20warning%20signals%2C%20moody%20atmospheric%20digital%20art%2C%20deep%20navy%20and%20charcoal%20tones%20with%20subtle%20red%20accents%2C%20data%20floating%20in%20vast%20dark%20space%2C%20cinematic%20sci-fi%20aesthetic%2C%20minimalist%20composition%20with%20strong%20negative%20space&width=500&height=400&seq=ax-story-1&orientation=squarish"
    },
    {
      labelKey: "storyContent.ch2.label",
      titleKey: "storyContent.ch2.title",
      bodyKeys: ["storyContent.ch2.body.0", "storyContent.ch2.body.1", "storyContent.ch2.body.2", "storyContent.ch2.body.3"],
      image: "https://readdy.ai/api/search-image?query=Precision%20CNC%20machined%20aluminum%20mechanical%20axis%20component%20with%20anodized%20silver%20finish%2C%20industrial%20product%20photography%20on%20dark%20concrete%20background%2C%20dramatic%20rim%20lighting%20highlighting%20metallic%20surface%20texture%2C%20high-end%20engineering%20aesthetic%2C%20shallow%20depth%20of%20field%2C%20warm%20side%20light%20creating%20depth&width=500&height=400&seq=ax-story-2&orientation=squarish"
    },
    {
      labelKey: "storyContent.ch3.label",
      titleKey: "storyContent.ch3.title",
      bodyKeys: ["storyContent.ch3.body.0", "storyContent.ch3.body.1", "storyContent.ch3.body.2"],
      image: "https://readdy.ai/api/search-image?query=Sleek%20minimalist%20aluminum%20electronic%20device%20with%20anodized%20silver%20finish%20placed%20on%20dark%20slate%20surface%2C%20product%20photography%20with%20dramatic%20lighting%2C%20precision%20engineering%20visible%20in%20every%20detail%2C%20studio%20quality%20with%20sharp%20metallic%20reflections%2C%20high-end%20industrial%20design%20aesthetic%2C%20moody%20atmosphere&width=500&height=400&seq=ax-story-3&orientation=squarish"
    },
    {
      labelKey: "storyContent.ch4.label",
      titleKey: "storyContent.ch4.title",
      bodyKeys: ["storyContent.ch4.body.0", "storyContent.ch4.body.1", "storyContent.ch4.body.2", "storyContent.ch4.body.3"],
      image: "https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20data%20flowing%20inward%20towards%20a%20central%20glowing%20axis%20point%2C%20particles%20of%20light%20converging%20into%20a%20bright%20core%2C%20deep%20space%20background%20with%20warm%20silver%20and%20red%20highlights%2C%20cinematic%20digital%20art%2C%20sense%20of%20gravity%20and%20convergence%2C%20elegant%20and%20powerful%20atmosphere%2C%20data%20sovereignty%20concept&width=500&height=400&seq=ax-story-4&orientation=squarish"
    }
  ];

  return (
    <section id="story-content" ref={sectionRef} className="py-20 md:py-28">
      <div className="px-6 md:px-12 lg:px-20 max-w-6xl mx-auto">
        <div className="space-y-20 md:space-y-28">
          {chapters.map((chapter, index) => (
            <div
              key={chapter.labelKey}
              className={`animate-in opacity-0 translate-y-6 transition-all duration-700 delay-${(index + 1) * 100} flex flex-col ${
                index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
              } gap-10 lg:gap-16 items-center`}
            >
              <div className="lg:w-1/2">
                <div className="rounded-2xl overflow-hidden bg-white border border-ax-silver-light">
                  <img
                    src={chapter.image}
                    alt={t(chapter.titleKey)}
                    className="w-full h-[320px] md:h-[380px] object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-1/2">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-ax-red font-serif text-sm tracking-widest">
                    {t(chapter.labelKey)}
                  </span>
                  <div className="h-px w-12 bg-ax-red/30" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ax-dark font-bold mb-6">
                  {t(chapter.titleKey)}
                </h3>
                <div className="space-y-4">
                  {chapter.bodyKeys.map((key, pIndex) => (
                    <p
                      key={key}
                      className={`text-ax-dark/60 text-sm md:text-base leading-relaxed ${
                        pIndex === 0 && chapter.bodyKeys.length > 1
                          ? "font-medium text-ax-dark/80"
                          : ""
                      }`}
                    >
                      {t(key)}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}